import { useEffect, useMemo, useState, useRef, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import Slider from "react-slick";
import styles from "./page2.module.sass";
import { NextArrow, PrevArrow } from "../components/sliderArrow";
import { Player, ControlBar, ForwardControl, ReplayControl } from "video-react";
import { Title } from "../components/charity/title";
import PropTypes from 'prop-types';
import { renderMedia, isVideoFile, isYouTubeUrl } from "../utils/mediaUtils";
import ImageModal from "../components/ImageModal";
import Carousel from "../components/Carousel";
import CarouselList from "../components/CarouselList";

function Page2({ articleContent, sliderContent, albumSliderContent, pageSliderContent, handleAlbumClick }) {
  const location = useLocation();
  const [nav1, setNav1] = useState();
  const [nav2, setNav2] = useState();
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [currentAlbumIndex, setCurrentAlbumIndex] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState("");
  const [modalImageAlt, setModalImageAlt] = useState("");
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const videoRef = useRef(null);
  const triggerRef = useRef(null);
  const [playedOnce, setPlayedOnce] = useState(false);
  const [isVideoVisible, setIsVideoVisible] = useState(false);
  const mainSliderContainerRef = useRef(null);

  // 切換到 /page2 或 id 變更時，強制滾動至頂部
  // 使用 useLayoutEffect + 雙重（含延遲）滾動，避免 slick 或其他元件初始化時把畫面推到底
  useLayoutEffect(() => {
    const scrollToTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    };

    scrollToTop();
    const t1 = setTimeout(scrollToTop, 0);
    const t2 = setTimeout(scrollToTop, 120);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [location.pathname, location.search]);

  // 依 order 排序相簿清單（升冪）
  const sortedAlbums = useMemo(() => {
    const list = Array.isArray(albumSliderContent) ? [...albumSliderContent] : [];
    return list.sort((a, b) => (Number(a?.order) || 0) - (Number(b?.order) || 0));
  }, [albumSliderContent]);

  // 當相簿索引改變時自動載入相簿內容
  const handleAlbumChange = (index) => {
    if (sortedAlbums && sortedAlbums[index]) {
      const albumId = sortedAlbums[index].id;
      // 模擬點擊事件來觸發 handleAlbumClick
      const fakeEvent = {
        preventDefault: () => {},
        stopPropagation: () => {},
        currentTarget: {
          dataset: { id: albumId },
          getAttribute: (name) => {
            if (name === 'data-id' || name === 'id') return albumId;
            return null;
          },
        },
      };
      handleAlbumClick(fakeEvent);
      setCurrentAlbumIndex(index);
    }
  };

  // 當相簿清單載入時，自動載入第一個相簿（已按 order 排序）
  useEffect(() => {
    if (sortedAlbums && sortedAlbums.length > 0 && currentAlbumIndex === 0) {
      handleAlbumChange(0);
    }
  }, [sortedAlbums]);

  const handleImageClick = (imageSrc, imageAlt) => {
    // 直接以當前主輪播索引作為彈窗起始索引（可對應圖片或影片）
    const safeIndex = currentSlideIndex || 0;
    setModalImageSrc(imageSrc);
    setModalImageAlt(imageAlt);
    setModalImageIndex(safeIndex);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    // 關閉彈窗時，再把外部輪播導到彈窗目前的圖片對應之原始索引
    const targetIndex = modalImageIndex;
    if (typeof targetIndex === 'number' && !Number.isNaN(targetIndex)) {
      if (nav1 && typeof nav1.slickGoTo === 'function') nav1.slickGoTo(targetIndex);
      if (nav2 && typeof nav2.slickGoTo === 'function') nav2.slickGoTo(targetIndex);
      setCurrentSlideIndex(targetIndex);
    }
    setIsImageModalOpen(false);
  };

  const handlePreviousImage = () => {
    if (!sliderContent || sliderContent.length === 0) return;
    const total = sliderContent.length;
    const newIndex = (modalImageIndex - 1 + total) % total;
    setModalImageIndex(newIndex);
    setModalImageSrc(sliderContent[newIndex].image);
    setModalImageAlt(sliderContent[newIndex].title || "");
  };

  const handleNextImage = () => {
    if (!sliderContent || sliderContent.length === 0) return;
    const total = sliderContent.length;
    const newIndex = (modalImageIndex + 1) % total;
    setModalImageIndex(newIndex);
    setModalImageSrc(sliderContent[newIndex].image);
    setModalImageAlt(sliderContent[newIndex].title || "");
  };

  useEffect(() => {
    const currentTrigger = triggerRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setIsVideoVisible(true);
          if (videoRef.current && !playedOnce) {
            setTimeout(() => {
              if (videoRef.current && videoRef.current.play) {
                const playPromise = videoRef.current.play();
                if (playPromise !== undefined) {
                  playPromise.catch(error => {
                    console.log('自動播放失敗:', error);
                  });
                }
                setPlayedOnce(true);
              }
            }, 100);
          }
        } else {
          setIsVideoVisible(false);
          if (videoRef.current && videoRef.current.pause) {
            videoRef.current.pause();
          }
        }
      },
      {
        rootMargin: "0px 0px -10% 0px",
        threshold: 0.3,
      }
    );

    if (currentTrigger) {
      observer.observe(currentTrigger);
    }

    return () => {
      if (currentTrigger) {
        observer.unobserve(currentTrigger);
      }
    };
  }, [playedOnce]);

  useEffect(() => {
    if (videoRef.current) {
      if (isVideoVisible && !playedOnce) {
        setTimeout(() => {
          if (videoRef.current && videoRef.current.play) {
            const playPromise = videoRef.current.play();
            if (playPromise !== undefined) {
              playPromise.catch(error => {
                console.log('自動播放失敗:', error);
              });
            }
            setPlayedOnce(true);
          }
        }, 200);
      } else if (!isVideoVisible && videoRef.current.pause) {
        videoRef.current.pause();
      }
    }
  }, [isVideoVisible, playedOnce]);

  // 主輪播切換時，暫停主視區域內所有影片
  useEffect(() => {
    if (!mainSliderContainerRef.current) return;
    const videos = mainSliderContainerRef.current.querySelectorAll('video');
    videos.forEach((vid) => {
      if (!vid.paused) {
        if (typeof vid.pause === 'function') {
          vid.pause();
        }
      }
    });
  }, [currentSlideIndex]);

  // 當 sliderContent（活動照片）資料因相簿切換而更新時，重置下方輪播到第 0 張
  useEffect(() => {
    if (Array.isArray(sliderContent) && sliderContent.length > 0) {
      if (nav1 && typeof nav1.slickGoTo === 'function') nav1.slickGoTo(0, true);
      if (nav2 && typeof nav2.slickGoTo === 'function') nav2.slickGoTo(0, true);
      setCurrentSlideIndex(0);
    }
  }, [sliderContent, nav1, nav2]);

  return (
    <main>
      <section className="h-[250px] m-auto pt-[90px] page-title">
        <Title 
          title={articleContent?.title || ''}
          subTitle={articleContent?.intro || ''}
        />
      </section>
      <section className="py-20 table:py-12 max-w-[1300px] mx-auto [*&>:not(:last-child)]:mb-16 [*&>:not(:last-child)]:table:mb-14 [*&>:not(:last-child)]:mobile:mb-10 mobile:py-10">
        <section>
          <div className="flex px-10 gap-10 flex-row table:flex-col table:px-8 table:items-center mobile:flex-col mobile:items-center mobile:gap-4 mobile:px-6">
            {articleContent?.image && articleContent.image !== "" && !isVideoFile(articleContent.image) ? (
              <div className="max-w-[50%] w-fit px-20">
                <img src={articleContent.image} alt={articleContent?.title || ''} />
              </div>
            ) : null}
            <div
              className="text-[#696969] font-normal text-xl leading-loose flex-1 mobile:leading-normal"
              dangerouslySetInnerHTML={{ __html: articleContent?.content || '' }}
            ></div>
          </div>
          {articleContent.image && articleContent.image !== "" && isVideoFile(articleContent.image) ? (
            <div ref={triggerRef} className="mt-10 relative group md-l:mx-10 cursor-pointer">
              <Player 
                ref={videoRef} 
                src={articleContent.image}
                autoPlay={false}
                muted={true}
                playsInline={true}
              >
                <ControlBar autoHide={true}>
                  <ReplayControl seconds={5} order={2.1} />
                  <ForwardControl seconds={5} order={3.1} />
                </ControlBar>
              </Player>
            </div>
          ) : null}
          {Array.isArray(pageSliderContent) && pageSliderContent.length > 0 ? (
            <div className="mx-auto px-8 table:px-6 mobile:px-4 mb-10 mt-8 mobile:mt-6">
              <Carousel
                items={pageSliderContent.map((s) => ({
                  type: s.fileType === "video" ? "video" : (s.fileType === "youtube" ? "youtube" : "image"),
                  src: s.fileURL,
                  alt: s.title || "",
                }))}
                autoplay={false}
                aspectClassName="aspect-[1920/1080]"
              />
            </div>
          ) : null}
        </section>
        {(sortedAlbums?.length || 0) > 1 ? (
          <section className="page-album-list pt-[44px]">
            <div className="text-center">
              <h2 className="text-[#3D3D3D] font-normal text-5xl mb-4 table:text-4xl table:mb-3 mobile:text-3xl mobile:mb-2">
                相簿清單
              </h2>
            </div>
            <div className="mx-auto px-8 table:px-6 mobile:px-4">
              <CarouselList
                items={sortedAlbums || []}
                currentIndex={currentAlbumIndex}
                onSelect={(idx) => handleAlbumChange(idx)}
                aspectClassName="aspect-[345/285]"
                followSelection={false}
              />
            </div>
          </section>
        ) : null}
        {(sliderContent?.length || 0) !== 0 && (
          <div>
            <div className="text-center mb-[44px] table:mb-14 mobile:mb-8">
              <h2 className="text-[#3D3D3D] font-normal text-5xl mb-4 table:text-4xl table:mb-3 mobile:text-3xl mobile:mb-2">
                活動照片
              </h2>
              <h3 className="text-[#696969] font-normal text-xl table:text-lg mobile:text-base">
                Activity photos
              </h3>
            </div>
            <div ref={mainSliderContainerRef} className="page2-slider w-[50%] shadow-xl mx-auto mb-12 table:w-[75%] mobile:w-[75%]">
              <Slider
                asNavFor={nav2}
                focusOnSelect={false}
                ref={(slider) => setNav1(slider)}
                className="m-auto"
                fade={true}
                accessibility={false}
                beforeChange={(oldIndex, newIndex) => setCurrentSlideIndex(newIndex)}
                prevArrow={<PrevArrow customClassName={styles.prevArrow} />}
                nextArrow={<NextArrow customClassName={styles.nextArrow} />}
              >
                {sliderContent.map((item, index) => (
                  <div key={`SliderMain${index}`}>
                    <div 
                      className="aspect-[739/416] w-full relative overflow-hidden cursor-pointer"
                      onClick={() => {
                        if (sliderContent[currentSlideIndex]?.image && !isVideoFile(sliderContent[currentSlideIndex].image) && !isYouTubeUrl(sliderContent[currentSlideIndex].image)) {
                          handleImageClick(
                            sliderContent[currentSlideIndex].image,
                            sliderContent[currentSlideIndex].title || ""
                          );
                        }
                      }}
                    >
                      {renderMedia(
                        item.image,
                        "",
                        "absolute top-0 bottom-0 left-0 right-0 w-auto h-full m-auto",
                        {},
                        false,
                        currentSlideIndex === index,
                        null,
                        false
                      )}
                    </div>
                    <h2 className="text-[#3D3D3D] font-semibold text-3xl table:text-2xl mobile:text-xl">
                      {item.title ? item.title : ""}
                    </h2>
                  </div>
                ))}
              </Slider>
            </div>
            <Slider
              asNavFor={nav1}
              ref={(slider) => setNav2(slider)}
              slidesToShow={5}
              infinite={sliderContent.length > 5}
              centerMode={sliderContent.length > 5}
              swipeToSlide={true}
              focusOnSelect={true}
              beforeChange={(oldIndex, newIndex) => setCurrentSlideIndex(newIndex)}
              className={"center"}
              prevArrow={null}
              nextArrow={null}
              accessibility={false}
              responsive={[
                {
                  breakpoint: 1350,
                  settings: {
                    slidesToShow: 4,
                    infinite: sliderContent.length > 4,
                    centerMode: sliderContent.length > 4,
                  },
                },
                {
                  breakpoint: 991,
                  settings: {
                    slidesToShow: 3,
                    infinite: sliderContent.length > 3,
                    centerMode: sliderContent.length > 3,
                  },
                },
                {
                  breakpoint: 766,
                  settings: {
                    slidesToShow: 2,
                    infinite: sliderContent.length > 2,
                    centerMode: sliderContent.length > 2,
                  },
                },
                {
                  breakpoint: 375,
                  settings: {
                    slidesToShow: 1,
                    infinite: sliderContent.length > 1,
                    centerMode: sliderContent.length > 1,
                  },
                },
              ]}
            >
              {sliderContent.map((item, index) => (
                <div key={`SliderSub${index}`}>
                  <div className="aspect-[720/483] w-full relative overflow-hidden">
                    {renderMedia(
                      item.image,
                      "",
                      "absolute top-0 left-0 bottom-0 right-0 w-auto h-full m-auto",
                      {},
                      false,
                      false,
                      null,
                      false
                    )}
                  </div>
                  <h2 className="text-[#3D3D3D] font-semibold text-2xl table:text-xl mobile:text-lg break-words p-1">
                    {item.title ? item.title : ""}
                  </h2>
                </div>
              ))}
            </Slider>
          </div>
        )}
      </section>
      <ImageModal
        isOpen={isImageModalOpen}
        imageSrc={modalImageSrc}
        imageAlt={modalImageAlt}
        onClose={closeImageModal}
        images={sliderContent}
        currentIndex={modalImageIndex}
        onPrevious={handlePreviousImage}
        onNext={handleNextImage}
      />
    </main>
  );
}

Page2.propTypes = {
  articleContent: PropTypes.shape({
    title: PropTypes.string,
    intro: PropTypes.string,
    image: PropTypes.string,
    content: PropTypes.string
  }).isRequired,
  sliderContent: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.string,
      title: PropTypes.string
    })
  ).isRequired,
  albumSliderContent: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string,
      image: PropTypes.string
    })
  ).isRequired,
  pageSliderContent: PropTypes.arrayOf(
    PropTypes.shape({
      fileURL: PropTypes.string,
      fileType: PropTypes.string,
      title: PropTypes.string,
    })
  ),
  handleAlbumClick: PropTypes.func.isRequired
};

export default Page2;
